<!--
  @file Map.vue
  @description Componente que permite la renderizacion del mapa de leaflet asi como las propiedades
  que se requieren para el mismo.

  @author Adrian Zayas <adrianzayas@unindetec.edu.mx>
  @date 06-03-2025
  @version 1.0.0
-->
<template>
    <div id="map" style="height: 300px;">
        <l-control id="imageControl" v-if="options.useControlLegend" :position="'bottomleft'">
            <img @click="toggleImageSize()" :src="options.imgUrl" alt="Marcadores dispositivos" class="legend-image" />
        </l-control>
        <l-tile-layer :url="mapProperties.url" :attribution="mapProperties.attribution"
            :subdomains="mapProperties.subdomains"></l-tile-layer>
        <slot></slot>
        <l-control-zoom position="bottomright" :zoomInTitle="mapProperties.zoomInTitle"
            :zoomOutTitle="mapProperties.zoomOutTitle"></l-control-zoom>
        <l-control-fullscreen :options="fullScreenOptions"></l-control-fullscreen>
        <LRuler v-if="options.useRuleTool" ref="ruler" :options="ruleProperties"></LRuler>
    </div>
</template>

<script>
import { LMap, LTileLayer, LControlZoom, LControl } from "vue2-leaflet";
import LControlFullscreen from "vue2-leaflet-fullscreen";
import LRuler from "vue2-leaflet-ruler";
import L from 'leaflet';
import 'leaflet-draw/dist/leaflet.draw.css';
import 'leaflet-draw';
import { LocateControl } from "leaflet.locatecontrol";
import "leaflet.locatecontrol/dist/L.Control.Locate.min.css";

import ConfiguracionesDataService from '../../services/ConfiguracionesDataService';
import Crypto from "../../services/Crypto";
import countriesCenter from "../../utils/countriesCenter.json"


const isUltrawide = globalThis.innerWidth >= 3150;
/** Estilo de icono por defecto */
const blackMarkerIcon = new L.Icon({
    iconUrl: require("leaflet/dist/images/marker-icon-black-2x.png"),
    // Si es Ultrawide usamos 50x82, si no, el estándar 25x41
    iconSize: isUltrawide ? [50, 82] : [25, 41],
    shadowSize: isUltrawide ? [90, 90] : [45, 45],
    // El anchor debe ser la mitad del ancho y el total del alto para que la punta sea precisa
    iconAnchor: isUltrawide ? [25, 82] : [12, 41],
    shadowAnchor: isUltrawide ? [30, 90] : [15, 45],
    popupAnchor: isUltrawide ? [0, -70] : [0, -32],
});

export default {
    components: {
        LMap,
        LTileLayer,
        LControlZoom,
        LControlFullscreen,
        LRuler,
        LControl,
        LocateControl,
    },
    props: {
        options: {
            useLocateControl: { type: Boolean, required: false, default: false, },
            useRuleTool: { type: Boolean, required: false, default: false, },
            useDrawTools: { type: Boolean, required: false, default: false, },
            useControlLegend: { type: Boolean, required: false, default: false, },
            useMarkLocation: { type: Boolean, required: false, default: false, },
            useUserLocation: { type: Boolean, required: false, default: false, },
            imgUrl: { type: String, required: false, default: '' },
        },
        draw: {
            color: { type: String, required: false, default: "#3388ff", },
        },
    },
    emits: ["geoData", "userLocation", "locationMarker", "map"],
    data() {
        let zoomInicial = 5;

        if (globalThis.innerWidth >= 3150) {
            zoomInicial = 6;
        }
        return {
            nZoomInicial: zoomInicial,
            selectedPaisId: "",

            /**
             * Objeto de configuración del mapa.
             *
             * Propiedades:
             * - `attribution`: Cadena que contiene HTML para la atribución. Incluye un enlace al copyright de OpenStreetMap y menciona a GoogleMaps.
             * - `zoom`: Nivel de zoom inicial del mapa.
             * - `maxZoom`: Nivel máximo de zoom permitido para el mapa.
             * - `center`: Centro geográfico inicial del mapa, especificado como latitud y longitud usando `L.latLng`.
             * - `subdomains`: Matriz de cadenas de subdominios utilizados para cargar los mosaicos del mapa.
             */
            mapProperties: {
                url: "https://{s}.google.com/vt/lyrs=m&x={x}&y={y}&z={z}",
                attribution: '&copy; <a href="https://osm.org/copyright"></a> Contributors, GoogleMaps',
                zoom: zoomInicial,
                maxZoom: 20,
                center: L.latLng(23.6345, -102.5528),
                subdomains: ["mt0", "mt1", "mt2", "mt3"],
                zoomInTitle: this.$t('Mapa.zoomInTitle'),
                zoomOutTitle: this.$t('Mapa.zoomOutTitle'),
            },
            /**
             * Objeto de configuración para las propiedades de las reglas en el componente mapa.
             *
             * Propiedades:
             * - `position`: Especifica la posición del control de la regla en el mapa. Por defecto es 'bottomright'.
             * - `circleMarker`: Configuración para el marcador de círculo utilizado en la regla.
             * - `lineStyle`: Configuración para el estilo de línea utilizado en la regla.
             * - `lengthUnit`: Configuración para la visualización de la unidad de longitud.
             * - `angleUnit`: Configuración para la visualización de la unidad angular.
             */
            ruleProperties: {
                position: 'bottomright',
                circleMarker: { color: 'blue', radius: 2 },
                lineStyle: { color: 'blue', dashArray: '1,6' },
                lengthUnit: { factor: null, display: 'km', decimal: 2, label: this.$t('distancia'), },
                angleUnit: { display: '&deg;', decimal: 2, factor: null, label: this.$t('angulo'), }
            },
            /**
             * Objeto de configuración para las propiedades del control de pantalla completa.
             * - `position`: Especifica la posición del control de la regla en el mapa. Por defecto es 'bottomright'.
             * - `title`: Objeto que contiene los títulos para el control de pantalla completa. El título cambia según el estado de pantalla completa.
             */
            fullScreenOptions: {
                position: 'bottomright',
                title: { 'false': this.$t('Mapa.screenFullTitle'), 'true': this.$t('Mapa.screenExitTitle'), }
            },
            /**
             * Objeto de configuración para las propiedades del control de ubicación.
             * - `position`: Especifica la posición del control de la regla en el mapa. Por defecto es 'topright'.
             * - `initialZoomLevel`: Nivel de zoom inicial al activar la ubicación.
             * - `flyTo`: Indica si el mapa debe moverse a la ubicación actual.
             * - `drawCircle`: Indica si se debe dibujar una sombra alrededor de la ubicación actual.
             * - `showPopup`: Indica si se debe mostrar un popup con información sobre la ubicación actual.
             * - `strings`: Objeto que contiene el título con la acción del botón.
             * - `locateOptions`: Opciones para la localización de alta precisión.
             */
            locationProperties: {
                position: 'topright',
                initialZoomLevel: 16,
                flyTo: true,
                drawCircle: false,
                showPopup: false,
                strings: { title: 'Activar ubicación actual' },
                locateOptions: { enableHighAccuracy: true }
            },
            /**
             * Objeto de configuración para las propiedades de la barra de herramientas de dibujo.
             * - `position`: Especifica la posición del control de la regla en el mapa. Por defecto es 'bottomright'.
             * - `edit`: Configuración para la edición de los elementos dibujados.
             * - `draw`: Configuración para las herramientas de dibujo disponibles.
             */
            drawToolBarProperties: {
                position: "bottomright",
                edit: { featureGroup: null, poly: { allowIntersection: false, }, remove: true, },
                draw: {
                    polyline: { allowIntersection: false, showArea: true, },
                    circle: true,
                    rectangle: false,
                    marker: false,
                    polygon: true,
                    circlemarker: false,
                },
            },
            /**
             * Objeto de configuración para las propiedades de renderización de poligonos en el mapa.
             * - `position`: Especifica la posición del control de la regla en el mapa. Por defecto es 'bottomright'.
             * - `edit`: Configuración para la edición de los elementos dibujados.
             * - `draw`: Configuración para las herramientas de dibujo disponibles.
             */
            drawMarkProperties: {
                position: "bottomright",
                edit: {
                    featureGroup: null,
                    poly: { allowIntersection: false, },
                },
                draw: {
                    polyline: { allowIntersection: false, showArea: false, },
                    circle: false,
                    rectangle: false,
                    marker: { icon: blackMarkerIcon, },
                    polygon: false,
                    circlemarker: false,
                }
            },
            /** Objeto para almacenar el contro para la renderizado de polígono(s) dentro del mapa.  */
            drawControl: null,
            /** Objeto para almacenar el control de la herramienta de dibujo. */
            toolBarControl: null,

            /** Objeto para almacenar la capa de los 'dibujos' sobre el mapa. */
            geofenceLayer: null,
            /** Objeto para almacenar la capa de los marcadores a renderizar sobre el mapa. */
            entityLayer: null,

            /** Objeto para almacenar el control de localización sobre el mapa. */
            locateControl: null,
            /** Variable de control para identificar si se esta usando la localización actual sobre el mapa. */
            isLocating: false,
            /** Variable de control para estado actual de la leyenda sobre el mapa: 
             * - `original`: cuando la leyenda se encuenta en estado 'maximizada'
             * - `small`: cuando la leyenda se encuenta en estado 'minimizada'
             * - `Valor por defecto: original`
             */
            imageSize: 'original',

            map: null,
            mapRef: null,
            baseLayerGroup: null,       // Para controlar el mapa base
            controlGroup: null,         // Para L.Control personalizados
            featureGroup: null,         // Para marker, polygon, circle, etc.

            renderedEntities: [],
        };
    },
    watch: {
        options: {
            handler(newVal) {
                if (newVal.useMarkLocation != null) {
                    if (newVal.useMarkLocation) this.initDrawLocationMark();
                    else this.destroyDrawLocationMark();
                }
                this.isLocating = newVal.useUserLocation;
                if (this.isLocating) this.focusLocation();
                if (newVal.useDrawTools) this.initDrawToolBar();
                else {
                    this.drawToolBarProperties.edit.featureGroup = null;
                    this.geofenceLayer.clearLayers();
                    this.map.removeLayer(this.geofenceLayer);
                    this.map.removeControl(this.toolBarControl);
                }
            },
            deep: true,
        },

        selectedCountry(newCountry) {
            if (!newCountry) return;

            // actualizamos mapProperties.center
            this.mapProperties.center = L.latLng(newCountry.lat, newCountry.lng);

            // si el mapa ya fue inicializado, movemos la vista
            if (this.map) {
                // nivel de zoom por defecto
                const defaultZoom = this.mapProperties.zoom || this.nZoomInicial;

                this.map.flyTo([newCountry.lat, newCountry.lng], defaultZoom, { animate: true, duration: 1 });
            }
        }
    },
    mounted: async function () {
        this.$nextTick(async () => {

            await this.getConfiguraciones()

            this.initMap();
            if (this.options.useMarkLocation) this.initDrawLocationMark();
            if (this.options.useDrawTools) this.initDrawToolBar();
        });
    },
    computed: {

        /**
         * Obtiene los datos del país segun el iso
         */
        selectedCountry() {
            return countriesCenter.find((c) => c.iso === this.selectedPaisId) || null;
        },

    },
    methods: {

        /**
         * Obtiene el iso registrado en configuracions generales
         */
        async getConfiguraciones() {
            try {
                const key = sessionStorage.getItem("key");

                const response = await ConfiguracionesDataService.getAll()
                const decoded = await Crypto.desencriptar(response.data, key)

                this.selectedPaisId = decoded[0].iso ?? "MX";

            } catch (error) {
                console.log('error', error);
            }
        },

        initMap() {
            // INICIALIZACIÓN DEL OBJETO MAPA
            this.map = L.map('map', {
                center: this.mapProperties.center,
                zoom: this.mapProperties.zoom,
                zoomControl: false,
            });
            this.$emit("mapReference", this.map);

            // INICIALIZACIÓN DE GRUPOS REFERENTES AL MAPA
            this.baseLayerGroup = L.layerGroup().addTo(this.map);
            this.controlGroup = L.layerGroup().addTo(this.map);
            this.featureGroup = L.featureGroup().addTo(this.map);

            // Se inicaliza la capa base agregando la capa (estilo) de google maps.
            L.tileLayer(this.mapProperties.url, {
                attribution: this.mapProperties.attribution,
                subdomains: this.mapProperties.subdomains,
            }).addTo(this.baseLayerGroup);

            this.$nextTick(() => {
                setTimeout(() => {
                    if (this.map) {
                        this.map.invalidateSize();
                        //console.log("Mapa redimensionado para eliminar zona gris");
                    }
                }, 300); // 300ms es el tiempo ideal
            });

            this.initControls();
        },
        initControls() {
            // Se incializa el control de zoom y se agrega al mapa.
            L.control.zoom({ position: 'bottomright', zoomInTitle: this.mapProperties.zoomInTitle, zoomOutTitle: this.mapProperties.zoomOutTitle }).addTo(this.map);
            // Se incializa el control de Fullscreen y se agrega al mapa.
            this.map.addControl(new L.Control.Fullscreen(this.fullScreenOptions));
            // Se incializa el control de localización y se agrega al mapa.
            this.initLocateControl();
            if (this.options.useRuleTool) this.initRulerControl();
        },



        initLocateControl() {
            this.locateControl = new LocateControl(this.locationProperties).addTo(this.map);
            this.map.on("locateactivate", this.handleLocationState);
            this.map.on("locatedeactivate", this.handleLocationState);
        },
        initRulerControl() {
            L.control.ruler(this.ruleProperties).addTo(this.map);
        },




        renderEntityOnMap(entity, popup = null, open = false) {
            let render;

            if (entity?.icon) {
                render = this.renderMarkerOnMap(entity);
            } else if (entity?.latlngs) {
                render = this.renderPolygonGeofence(entity);
            } else {
                render = this.renderCircleGeofence(entity);
            }

            if (popup) {
                render.bindPopup(popup);
                if (open) render.openPopup();
            }

            this.renderedEntities.push(render);

            return render;
        },
        renderMarkerOnMap(marker) {
            const { latlng, icon } = marker;

            const m = new L.Marker([latlng.lat, latlng.lng], { icon }).addTo(this.featureGroup);

            // 👇 Guardar latlng real en el marker
            m.latlng = latlng;

            return m;
        },
        renderCircleGeofence(geofence) {
            const { lat, lng, radio, color } = geofence;
            return new L.Circle([lat, lng], radio, { color: color, fillColor: color, }).addTo(this.featureGroup);
        },
        renderPolygonGeofence(geofence) {
            const { latlngs, color } = geofence;
            return new L.Polygon(latlngs, { color: color, fillColor: color, }).addTo(this.featureGroup);
        },
        focusPositionOnMap(latlng, zoom) {
            if (!latlng) return;

            // aceptar latlng de Leaflet o tu propio objeto
            const pos = latlng.lat ? latlng : latlng.getLatLng();

            this.map.flyTo([pos.lat, pos.lng], zoom);
        },
        removeEntityFromMap(entity, zoomDefault = false) {
            if (Array.isArray(entity)) {
                entity.forEach(item => {
                    const layerToRemove = item?.marker || item;
                    if (this.map.hasLayer(layerToRemove)) {
                        this.map.removeLayer(layerToRemove);
                    }
                });
            }
            if (entity && typeof entity === "object") {
                const layerToRemove = entity?.marker || entity;
                if (this.map.hasLayer(layerToRemove)) {
                    this.map.removeLayer(layerToRemove);
                }
            }

            if (zoomDefault) {
                this.map.flyTo([22.63429269, -101.42578125], 5, { animate: true, duration: 2 });
            }
        },
        clearMapContent() {
            if (this.featureGroup) {
                this.featureGroup.clearLayers();
                this.featureGroup.remove();
                this.featureGroup = null;
            }

        },
        nuevoZoom(shapes) {
            if (!shapes || shapes.length === 0) return;

            if (shapes.length === 1) {
                const shape = shapes[0];

                if (shape instanceof L.Circle) {
                    const center = shape.getLatLng();
                    const radius = shape.getRadius();

                    const getZoomLevelForRadius = radius => {
                        const zoom = 18 - Math.log2(radius / 100);
                        return Math.max(1, Math.min(zoom, 18));
                    };

                    const zoom = getZoomLevelForRadius(radius);
                    this.map.flyTo(center, zoom);

                } else if (shape instanceof L.Marker) {
                    const latlng = shape.getLatLng();
                    this.map.flyTo(latlng, 18);

                } else if (typeof shape.getBounds === 'function') {
                    const bounds = shape.getBounds();
                    const center = bounds.getCenter();
                    const zoom = this.map.getBoundsZoom(bounds);
                    this.map.flyTo(center, zoom);
                }
            } else {
                const bounds = L.latLngBounds([]);
                shapes.forEach(shape => {
                    if (typeof shape.getBounds === 'function') {
                        bounds.extend(shape.getBounds());
                    } else if (shape.getLatLng) {
                        bounds.extend(shape.getLatLng());
                    }
                });

                this.map.flyToBounds(bounds, {
                    padding: [40, 40],
                });
            }
        },

        fitBoundsOnMap(bounds, padding = [40, 40]) {
            if (!this.map) return;

            // bounds puede venir como array de lat-lng normales
            const leafletBounds = Array.isArray(bounds)
                ? L.latLngBounds(bounds)
                : bounds;

            this.map.fitBounds(leafletBounds, { padding });
        },





        /**
         * Método encargado de manejar el estado de la localización del usuario.
         * Cambia el estado de la variable isLocating y emite un evento con el nuevo estado.
         * - Si el estado es falso, se centra el mapa en la ubicación inicial.
         */
        handleLocationState() {
            this.isLocating = !this.isLocating;
            if (!this.isLocating) this.map.flyTo([this.mapProperties.center.lat, this.mapProperties.center.lng], this.mapProperties.zoom);
            this.$emit("userLocation", this.isLocating);
        },
        /**
         * Método encargado de centrar el mapa en la ubicación actual del usuario.
         * Se utiliza el control de localización para iniciar la búsqueda de la ubicación.
         */
        focusLocation() {
            if (this.locateControl) this.locateControl.start();
        },

        /**
         * Método encargado de inicializar la barra de herramientas de dibujo en el mapa (geocercas).
         * Se utiliza la librería leaflet.draw para crear un control de dibujo.
         */
        initDrawToolBar() {

            this.geofenceLayer = new globalThis.L.FeatureGroup().addTo(this.map);
            this.drawToolBarProperties.edit.featureGroup = this.geofenceLayer;
            this.toolBarControl = new globalThis.L.Control.Draw(this.drawToolBarProperties);
            this.map.addControl(this.toolBarControl);
            this.map.on(L.Draw.Event.CREATED, (event) => { this.onDrawCreated(event) });
        },
        /**
         * Método encargado de manejar el evento de creación de un nuevo polígono (geocerca) sobre el mapa.
         * Se emite un evento con la información del polígono creado.
         * @param {Object} event - Evento que contiene información sobre el polígono creado.
         */
        onDrawCreated(event) {
            if (this.geofenceLayer.getLayers().length == 0) {
                const layer = event.layer;
                layer.setStyle({ color: this.draw?.color || "#3388ff" });
                this.geofenceLayer.addLayer(layer);
                layer.on("edit", () => { this.onDrawEdited(event) });
                this.emitCoordinates(event);
            } else {
                this.renderAlertMessage();
            }
        },
        /**
         * Método encargado de manejar el evento de edición de un polígono (geocerca) sobre el mapa.
         * Se emite un evento con la información del polígono editado.
         * @param {Object} event - Evento que contiene información sobre el polígono editado.
         */
        onDrawEdited(event) {
            console.log('Editado')

            const draw = event.layer;
            const geoJson = draw.toGeoJSON();
            let locationDetails = {};
            locationDetails.coordinates = geoJson.geometry.coordinates;
            locationDetails.type = geoJson.geometry.type;
            locationDetails.layer = event.layer;
            if (locationDetails.type === 'Point') {
                locationDetails.radiusConfig = {};
                locationDetails.radiusConfig.radius = draw.getRadius() < 999 ? draw.getRadius().toFixed() : (draw.getRadius() / 1000).toFixed(2);
                locationDetails.radiusConfig.unit = draw.getRadius() < 999 ? 'meters' : 'km';
            }
            this.$emit("geoData", locationDetails);
        },
        /**
         * Método encargado de emitir las coordenadas del polígono (geocerca) creado o editado.
         * Se emite un evento con la información del polígono.
         * @param {Object} event - Evento que contiene información sobre el polígono creado o editado.
         */
        emitCoordinates(event) {
            try {
                const draw = event.layer;
                const geoJson = draw.toGeoJSON();
                const coordinates = geoJson.geometry.coordinates;

                //El siguiente bloque de código funciona, sin embargo no se usa de momento ya que en la bd dentro de la coleccion los documentos ya tienen el locate establecido
                // const coordinates = geoJson.geometry.coordinates.length === 2
                //     ? geoJson.geometry.coordinates.reverse() 
                //     : geoJson.geometry.coordinates[0].map(latlng => latlng.reverse());

                const geoJSON = {
                    coordinates,
                    type: geoJson.geometry.type,
                    layer: event.layer,
                    radiusConfig: geoJson.geometry.type === 'Point' ? {
                        radius: draw.getRadius() < 999 ? draw.getRadius().toFixed(2) : (draw.getRadius() / 1000).toFixed(2),
                        unit: draw.getRadius() < 999 ? 'meters' : 'km'
                    } : undefined
                };
                this.$emit("geoData", geoJSON);
            } catch (err) {
                console.error("Error en la funcion emitCoordinates: ", err)
            }

        },

        /**
         * Método encargado de inicializar el control de marcadores (dispositivo o ubicación de interés) en el mapa.
         * Se utiliza la librería leaflet.draw para crear un control de dibujo.
         */
        initDrawLocationMark() {
            this.entityLayer = new globalThis.L.FeatureGroup().addTo(this.map);
            // Forzamos el uso de nuestro icono escalado en la herramienta de dibujo
            this.drawMarkProperties.draw.marker.icon = blackMarkerIcon;

            this.drawMarkProperties.edit.featureGroup = this.entityLayer;

            this.drawControl = new globalThis.L.Control.Draw(this.drawMarkProperties);
            this.map.addControl(this.drawControl);

            this.map.on(L.Draw.Event.CREATED, (event) => { this.onLocationCreated(event) });
            this.map.on(L.Draw.Event.EDITED, (event) => { this.onLocationEdited(event) });
        },
        /**
         * Método encargado de manejar el evento de creación de un nuevo marcador (geocerca) sobre el mapa.
         * Se emite un evento con la información del marcador creado.
         * @param {Object} event - Evento que contiene información sobre el marcador a crear.
         */
        onLocationCreated(event) {
            if (this.entityLayer.getLayers().length == 0) {
                const layer = event.layer;
                this.entityLayer.addLayer(layer);
                const latlng = layer.getLatLng();
                const marker = {
                    lat: Number.parseFloat(latlng.lat).toFixed(8),
                    lng: Number.parseFloat(latlng.lng).toFixed(8),
                    layer: layer,
                }
                this.$emit("locationMarker", marker);
            }
        },
        /**
         * Método encargado de manejar el evento de edición de un marcador (geocerca) sobre el mapa.
         * Se emite un evento con la información del marcador editado.
         * @param {Object} event - Evento que contiene información sobre el marcador a editar.
         */
        onLocationEdited(event) {
            const layers = event.layers;
            let marker = {};
            layers.eachLayer(function (layer) {
                const latlng = layer.getLatLng();
                marker = {
                    lat: Number.parseFloat(latlng.lat).toFixed(8),
                    lng: Number.parseFloat(latlng.lng).toFixed(8),
                    layer: layer,
                }
            })
            this.$emit("locationMarker", marker);
        },
        /**
         * Método encargado de remover el control de marcadores (dispositivo o ubicación de interés) en el mapa.
         */
        destroyDrawLocationMark() {
            this.map.removeControl(this.drawControl);
        },

        renderControlEdit() {
            const editControl = new L.Control.Draw({
                position: "bottomright",
                edit: {
                    featureGroup: this.featureGroup,
                    remove: false,
                    poly: {
                        allowIntersection: false,
                    },
                },
                draw: false,
            });
            this.map.addControl(editControl);

            return { control: editControl, layer: this.featureGroup }
        },
        /**
         * Método encargado de asignar el nuevo estado de la leyenda (maximizada o minimizada).
         * Cambia el tamaño de la leyenda y actualiza su estado.
         * - `original`: cuando la leyenda se encuenta en estado 'maximizada'
         * - `small`: cuando la leyenda se encuenta en estado 'minimizada'
         */
        toggleImageSize() {
            this.imageSize = this.imageSize === 'original' ? 'small' : 'original';
            this.updateImageSize();
        },
        /**
         * Método encargado de actualizar el tamaño de la leyenda (maximizada o minimizada).
         * Cambia el tamaño de la leyenda según el estado actual.
         */
        updateImageSize() {
            const imageControl = document.getElementById('imageControl');
            if (imageControl) {
                imageControl.style.transform = this.imageSize === 'small' ? 'scale(0.675)' : 'scale(1.30)';
            }
        },

        /**
         * Método encargado de renderizar un mensaje de alerta en el mapa.
         * Se utiliza un control de Leaflet para mostrar el mensaje durante 5 segundos.
         */
        renderAlertMessage() {
            if (!this.map) {
                console.warn("Mapa aún no está disponible");
                return;
            }

            const alertControl = L.control({ position: 'topright' });
            alertControl.onAdd = function () {
                const div = L.DomUtil.create('div', 'sweet-alert-control');
                div.innerHTML = `
                    <div class="alert alert-danger" role="alert" style="font-size: 1.5em; padding: 1em;">
                    Solo se puede dibujar una geocerca
                    </div>
                `;
                return div;
            };
            alertControl.addTo(this.mapReference);
            setTimeout(() => this.mapReference.removeControl(alertControl), 5000);
        },

        clearEntities() {
            if (this.renderedEntities.length) {
                this.removeEntityFromMap(this.renderedEntities);
                this.renderedEntities = [];
            }
        },

        /**
         * Metodo que recetea el zoom del mapa con coordenadas por default.
         */
        resetZoom() {
            this.map.setView([19.0465395, -95.97902683333334], 5); // lat, lng y defaultZoom los defines
        },

    },//FIN Methods
};
</script>

<style>
#map {
    height: 100% !important;
    width: 100% !important;
    z-index: 1;
}

#mapaStyle {
    position: static;
    width: 100%;
    height: 600px !important;
    z-index: 1;
}

#imageControl {
    position: absolute;
    bottom: 20px;
    left: 20px;
    z-index: 1000;
    padding: 5px;
    cursor: pointer;
    transform: scale(1.30);
    transition: transform 0.3s ease;
    /* Transición suave */
}

.layered.box {
    box-shadow: 0 1px 1px hsl(0deg 0% 0% / 0.075),
        0 2px 2px hsl(0deg 0% 0% / 0.075), 0 4px 4px hsl(0deg 0% 0% / 0.075),
        0 8px 8px hsl(0deg 0% 0% / 0.075), 0 16px 16px hsl(0deg 0% 0% / 0.075);
}

.box {
    border-radius: 2px;
    background-color: white;
}

.alertStyle {
    font-size: 18px;
    font-weight: 500;
}

/* Tamaño base de la leyenda (imagen) */
.legend-image {
    max-width: 100px;
    transition: transform 0.3s ease;
    cursor: pointer;
}

/* --- ESCALADO PARA PANTALLAS ULTRAWIDE (3440px) --- */
@media (min-width: 3150px) {

    /* Escalamos la imagen de la leyenda para que sea visible en 4K/Ultrawide */
    .legend-image {
        max-width: 220px;
        padding: 1rem;
    }

    /* Ajuste general para todos los botones de herramientas de dibujo/edición */
    .leaflet-draw-toolbar a {
        background-size: 600px 60px !important;
        width: 60px !important;
        height: 60px !important;
        background-repeat: no-repeat !important;
        line-height: 60px !important;
        font-size: 60px !important;
    }

    /* Escalamos los iconos de control de Leaflet (zoom, fullscreen, etc) */
    .leaflet-control-zoom-in,
    .leaflet-control-zoom-out,
    .leaflet-bar a,
    .leaflet-control-locate a {
        width: 60px !important;
        height: 60px !important;
        line-height: 60px !important;
        font-size: 40px !important;
    }

    /* Contenedor del botón */
    .leaflet-control-fullscreen-button {
        width: 60px !important;
        height: 60px !important;
        background-size: 60px 120px !important;
        background-position: 0 0 !important;
        /* El doble del alto para los dos estados */
    }

    /* Estado: Activo (Cuando YA está en Fullscreen, para salir) */
    /* Leaflet suele añadir una clase o pseudo-clase cuando está activo */
    .leaflet-fullscreen-on .leaflet-control-fullscreen-button,
    .leaflet-control-fullscreen-button.leaflet-fullscreen-on {
        background-position: 0 -60px !important;
    }

    .leaflet-control-locate a .leaflet-control-locate-location-arrow {
        width: 40px;
        height: 45px;
        /* Centrado manual si es necesario */
    }

    /**Drawer */

    /* Corregimos la posición individual de cada icono si se ven movidos */
    /* Nota: Los valores de px pueden variar un poco según tu plugin exacto*/
    .leaflet-draw-draw-polyline {
        background-position: 0 0 !important;
    }

    .leaflet-draw-draw-polygon {
        background-position: -60px 0 !important;
    }

    .leaflet-draw-draw-rectangle {
        background-position: -120px 0 !important;
    }

    .leaflet-draw-draw-circle {
        background-position: -180px 0 !important;
    }

    .leaflet-draw-draw-marker {
        background-position: -240px 0 !important;
    }

    .leaflet-draw-draw-circlemarker {
        background-position: -300px 0 !important;
    }

    .leaflet-draw-edit-edit {
        /* Si antes -360 editaba, probemos con la posición de la basura */
        background-position: -420px 0 !important;
    }

    .leaflet-draw-edit-remove {
        /* Si antes -420 borraba, ahora ponemos la que tenía el lápiz */
        background-position: -360px 0 !important;
    }

    /* Si los iconos se ven duplicados verticalmente: */
    .leaflet-control-locate a {
        display: flex !important;
        align-items: center;
        justify-content: center;

    }

    .leaflet-draw-actions {
        li a {
            height: 40px !important;
            line-height: 40px !important;
            font-size: 1.4rem !important;
            padding: 0 15px !important;
            background-color: #555 !important;
        }
    }

    .leaflet-draw-actions-top.leaflet-draw-actions-bottom a {
        height: 3rem;
        line-height: 3rem;
    }

    .leaflet-right .leaflet-draw-actions li:first-child a {
        font-size: 1.6rem;
    }

    .leaflet-control-attribution {
        font-size: 1.6rem;
    }

    /** --- MARKER's --- */
    .leaflet-marker-icon {
        width: 80px !important;
        /* El doble del estándar */
        height: 90px !important;
        /* El doble del estándar */

        /* IMPORTANTE: El margen debe ser la mitad del ancho (negativo) 
           y el total del alto (negativo) para que la punta coincida con la coordenada */
        margin-left: -40px !important;
        margin-top: -82px !important;
    }

    /* Escalamos la sombra del PIN */
    .leaflet-marker-shadow {
        width: 82px !important;
        height: 82px !important;
        margin-left: -25px !important;
        margin-top: -82px !important;
    }

    /* Si usas círculos azules de Leaflet.draw para editar (los nodos) */
    .leaflet-div-icon,
    .leaflet-editing-icon {
        width: 24px !important;
        height: 24px !important;
        margin-left: -12px !important;
        margin-top: -12px !important;
        background: #fff !important;
        border: 3px solid #007bff !important;
    }

    /* Los cuadritos azules de edición al rededor de círculos/polígonos */
    .leaflet-editing-icon {
        width: 24px !important;
        height: 24px !important;
        margin-left: -12px !important;
        margin-top: -12px !important;
        border-radius: 4px !important;
        border: 3px solid white !important;
        box-shadow: 0 0 10px rgba(0, 0, 0, 0.5) !important;
    }

    /* La línea que se ve mientras dibujas una polilínea */
    .leaflet-draw-guide-dash {
        background-color: rgba(0, 0, 0, 0.6) !important;
    }

    /* Contenedor del globo de texto */
    .leaflet-popup-content-wrapper {
        font-size: 1.1rem !important;
        line-height: 2.2rem !important;
        border-radius: 15px !important;
        padding: 10px !important;
    }

    /* El texto interno del popup */
    .leaflet-popup-content {
        width: 350px !important;
        /* Más ancho para que el texto respire */
        margin: 20px !important;
    }

    /* El botón de cerrar (X) del popup */
    .leaflet-popup-close-button {
        padding: 12px 12px 0 0 !important;
        font-size: 2rem !important;
    }

}
</style>
